import { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { Button, Toast, Spinner } from '@core/components/ui';
import { logger } from '@core/utils';

import { useGetFoldersQuery, useCreateFolderMutation, useUpdateFolderMutation, useDeleteFolderMutation } from '../api/foldersApi';
import { useGetFilesQuery, useLazyGetDownloadUrlQuery, useDeleteFileMutation, useMoveFileToFolderMutation } from '../api/filesApi';
import { useFileUpload } from '../hooks/useFileUpload';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { FileUploadProgress, FileDeleteModal, FileTable, CreateFolderModal, RenameFolderModal, MoveFileModal } from '../components';

/**
 * UnifiedFileExplorer Component
 *
 * Unified file and folder explorer similar to Google Drive.
 * Shows folders and files in the same list with navigation support.
 *
 * @param {Object} props
 * @param {string} props.accountId - Account ID for file ownership
 * @param {string|null} [props.segmentedKnowledgeId] - Segmented knowledge ID (optional)
 * @param {boolean} [props.showUploader=true] - Whether to show upload zone
 * @param {Function} [props.onNavigationChange] - Callback when folder navigation changes (receives folder object or null)
 * @param {number} [props.resetTrigger] - Timestamp to trigger navigation reset
 * @param {Function} [props.onDragHandlersReady] - Callback to expose drag handlers to parent (receives {dragHandlers, isDragging})
 */
const UnifiedFileExplorer = ({ accountId, segmentedKnowledgeId = null, showUploader = true, onNavigationChange, resetTrigger, onDragHandlersReady }) => {
  const { t } = useTranslation();

  // Navigation state
  const [currentFolderId, setCurrentFolderId] = useState(null);

  // Reset navigation when resetTrigger changes
  useEffect(() => {
    if (resetTrigger) {
      setCurrentFolderId(null);
    }
  }, [resetTrigger]);

  // UI state
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [fileToMove, setFileToMove] = useState(null);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // API Queries
  const { data: folders = [], isLoading: loadingFolders, refetch: refetchFolders } = useGetFoldersQuery({
    segmentedKnowledgeId: segmentedKnowledgeId || 'general',
  });

  const { data: files = [], isLoading: loadingFiles } = useGetFilesQuery({
    segmentedKnowledgeId,
    folderId: currentFolderId,
  });

  // API Mutations
  const [createFolder, { isLoading: isCreatingFolder }] = useCreateFolderMutation();
  const [updateFolder, { isLoading: isUpdatingFolder }] = useUpdateFolderMutation();
  const [deleteFolder] = useDeleteFolderMutation();
  const [getDownloadUrl] = useLazyGetDownloadUrlQuery();
  const [deleteFile] = useDeleteFileMutation();
  const [moveFileToFolder, { isLoading: isMovingFile }] = useMoveFileToFolderMutation();

  // File upload hook
  const {
    isDragging,
    uploadProgress,
    uploadingFiles,
    dragHandlers,
    handleFileInput,
  } = useFileUpload({
    accountId,
    segmentedKnowledgeId,
    folderId: currentFolderId,
    onSuccess: () => {
      // Refetch folders to update file_count after uploading a file
      refetchFolders();
      setToastMessage(t('files.explorer.uploadSuccess', 'Archivos subidos exitosamente'));
      setToastType('success');
    },
    onError: (err) => {
      logger.error('Upload error:', err);
      setToastMessage(t('files.explorer.uploadError', 'Error al subir archivos'));
      setToastType('error');
    },
  });

  // Drag and drop hook
  const {
    draggingFileId,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop({
    onSuccess: () => {
      // Refetch folders to update file_count after drag and drop move
      refetchFolders();
      setToastMessage(t('files.explorer.moveSuccess', 'Archivo movido exitosamente'));
      setToastType('success');
    },
    onError: (err) => {
      setToastMessage(err);
      setToastType('error');
    },
  });

  // Expose drag handlers to parent (re-notify when folderId changes)
  useEffect(() => {
    if (onDragHandlersReady && showUploader) {
      onDragHandlersReady({ dragHandlers, isDragging });
    }
  }, [onDragHandlersReady, dragHandlers, isDragging, showUploader, currentFolderId]);

  // Combine, sort and filter items (folders first, then files)
  const sortedItems = useMemo(() => {
    let items;

    // If viewing a folder, only show files from that folder
    if (currentFolderId) {
      items = files
        .map(f => ({ type: 'file', ...f }))
        .sort((a, b) => a.original_filename.localeCompare(b.original_filename));
    } else {
      // At root: show folders + files
      const folderItems = folders
        .map(f => ({ type: 'folder', ...f }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const fileItems = files
        .map(f => ({ type: 'file', ...f }))
        .sort((a, b) => a.original_filename.localeCompare(b.original_filename));

      items = [...folderItems, ...fileItems];
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(item => {
        const name = item.type === 'folder' ? item.name : item.original_filename;
        return name.toLowerCase().includes(query);
      });
    }

    return items;
  }, [folders, files, currentFolderId, searchQuery]);

  // Navigation handlers
  const handleFolderClick = useCallback((folder) => {
    setCurrentFolderId(folder.id);
    if (onNavigationChange) {
      onNavigationChange(folder);
    }
  }, [onNavigationChange]);

  const handleBackClick = useCallback(() => {
    setCurrentFolderId(null);
    if (onNavigationChange) {
      onNavigationChange(null);
    }
  }, [onNavigationChange]);

  // Create folder handler
  const handleCreateFolder = useCallback(async (folderName) => {
    const folderData = {
      name: folderName.trim(),
    };

    if (segmentedKnowledgeId && segmentedKnowledgeId !== 'general') {
      folderData.segmented_knowledge = segmentedKnowledgeId;
    }

    await createFolder(folderData).unwrap();
    setCreateFolderModalOpen(false);
    setToastMessage(t('files.folders.created', 'Carpeta creada exitosamente'));
    setToastType('success');
  }, [segmentedKnowledgeId, createFolder, t]);

  // Download handler
  const handleDownload = useCallback(async (file) => {
    try {
      const result = await getDownloadUrl(file.id).unwrap();
      const downloadUrl = result.download_url;

      if (!downloadUrl) {
        throw new Error('No download URL provided');
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      logger.error('Download error:', err);
      setToastMessage(t('files.explorer.downloadError', 'Error al descargar archivo'));
      setToastType('error');
    }
  }, [getDownloadUrl, t]);

  // Rename handler
  const handleStartRename = useCallback((folder) => {
    setRenamingFolder(folder);
    setRenameModalOpen(true);
  }, []);

  const handleRename = useCallback(async (newName) => {
    if (!renamingFolder) return;

    try {
      await updateFolder({
        id: renamingFolder.id,
        name: newName,
      }).unwrap();
      setRenameModalOpen(false);
      setRenamingFolder(null);
      setToastMessage(t('files.folders.updated', 'Carpeta renombrada exitosamente'));
      setToastType('success');
    } catch (err) {
      logger.error('Error renaming folder:', err);
      setToastMessage(err.data?.error || t('files.folders.updateError', 'Error al renombrar carpeta'));
      setToastType('error');
      throw err; // Re-throw to let modal handle error display
    }
  }, [renamingFolder, updateFolder, t]);

  const handleRenameCancel = useCallback(() => {
    setRenameModalOpen(false);
    setRenamingFolder(null);
  }, []);

  // Delete handler
  const handleDeleteClick = useCallback((item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete) return;

    // Frontend validation: prevent deleting folders with files
    if (itemToDelete.type === 'folder' && itemToDelete.file_count > 0) {
      setToastMessage(t('files.explorer.cannotDeleteFolderWithFiles', 'No se pueden eliminar carpetas que contienen archivos. Mueve los archivos antes de eliminar'));
      setToastType('error');
      setDeleteModalOpen(false);
      setItemToDelete(null);
      return;
    }

    try {
      if (itemToDelete.type === 'folder') {
        await deleteFolder(itemToDelete.id).unwrap();
      } else {
        await deleteFile(itemToDelete.id).unwrap();
        // Refetch folders to update file_count after deleting a file
        refetchFolders();
      }

      setToastMessage(t('files.explorer.deleteSuccess', 'Elemento eliminado exitosamente'));
      setToastType('success');
    } catch (err) {
      logger.error('Delete error:', err);
      setToastMessage(err.data?.error || t('files.explorer.deleteError', 'Error al eliminar'));
      setToastType('error');
    } finally {
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  }, [itemToDelete, deleteFolder, deleteFile, refetchFolders, t]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  }, []);

  // Move handler
  const handleMove = useCallback((file) => {
    setFileToMove(file);
    setMoveModalOpen(true);
  }, []);

  const handleMoveConfirm = useCallback(async (targetFolderId) => {
    if (!fileToMove) return;

    try {
      await moveFileToFolder({
        fileId: fileToMove.id,
        folderId: targetFolderId,
      }).unwrap();
      // Refetch folders to update file_count after moving a file
      refetchFolders();
      setMoveModalOpen(false);
      setFileToMove(null);
      setToastMessage(t('files.explorer.moveSuccess', 'Archivo movido exitosamente'));
      setToastType('success');
    } catch (err) {
      logger.error('Move error:', err);
      setToastMessage(err.data?.error || t('files.explorer.moveError', 'Error al mover archivo'));
      setToastType('error');
      throw err; // Re-throw to let modal handle error display
    }
  }, [fileToMove, moveFileToFolder, refetchFolders, t]);

  const handleMoveCancel = useCallback(() => {
    setMoveModalOpen(false);
    setFileToMove(null);
  }, []);

  // Row click handler
  const handleRowClick = useCallback((item) => {
    if (item.type === 'folder') {
      handleFolderClick(item);
    }
    // Files don't trigger navigation on row click (only context menu actions)
  }, [handleFolderClick]);

  // Get current folder name for breadcrumbs
  const currentFolder = useMemo(() => {
    if (!currentFolderId) return null;
    return folders.find(f => f.id === currentFolderId);
  }, [currentFolderId, folders]);

  // Check if content is empty
  const isEmpty = sortedItems.length === 0;

  // Loading state
  if (loadingFolders || loadingFiles) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {/* Unified Toolbar - Compact single line */}
      <div className="mb-6 flex items-center justify-between">
        {/* Left side - Back button (only in folders) + Search */}
        <div className="flex items-center gap-3">
          {currentFolderId && (
            <Button
              onClick={handleBackClick}
              variant="ghost"
              size="sm"
              className="cursor-pointer"
            >
              <Icon icon="mdi:arrow-left" className="w-5 h-5" />
            </Button>
          )}

          {/* Compact Search Input */}
          <div className="relative w-64">
            <Icon
              icon="lucide:search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted size-4"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('file.search', 'Buscar archivos...')}
              className="w-full pl-9 pr-9 py-2 text-sm border border-border-default rounded-lg focus:ring-2 focus:ring-brand focus:border-brand transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-default transition-colors cursor-pointer"
                aria-label={t('common.clearSearch', 'Limpiar búsqueda')}
              >
                <Icon icon="lucide:x" className="size-4" />
              </button>
            )}
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-3">
          {!currentFolderId && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCreateFolderModalOpen(true)}
              className="cursor-pointer"
            >
              <Icon icon="lucide:folder-plus" className="w-5 h-5 mr-2" />
              {t('files.explorer.newFolder', 'Nueva Carpeta')}
            </Button>
          )}

          {showUploader && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => document.getElementById('file-upload-input')?.click()}
              disabled={uploadingFiles.size > 0}
              className="cursor-pointer"
            >
              <Icon icon="mdi:upload" className="w-5 h-5 mr-2" />
              {t('files.explorer.uploadFiles', 'Subir Archivos')}
            </Button>
          )}

          {/* Hidden file input */}
          {showUploader && (
            <input
              id="file-upload-input"
              type="file"
              multiple
              onChange={handleFileInput}
              className="hidden"
            />
          )}
        </div>
      </div>

      {/* Upload Progress */}
      {uploadingFiles.size > 0 && (
        <div className="mb-4">
          <FileUploadProgress
            uploadingFiles={uploadingFiles}
            uploadProgress={uploadProgress}
          />
        </div>
      )}

      {/* Unified List */}
      <FileTable
        files={sortedItems}
        onDownload={handleDownload}
        onDelete={handleDeleteClick}
        onFolderClick={handleFolderClick}
        onRename={handleStartRename}
        onMove={handleMove}
        dragAndDropHandlers={{
          handleDragStart,
          handleDragOver,
          handleDrop,
          handleDragEnd,
          draggingFileId,
        }}
        downloadingFile={null}
      />

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={createFolderModalOpen}
        onClose={() => setCreateFolderModalOpen(false)}
        onCreate={handleCreateFolder}
        isCreating={isCreatingFolder}
      />

      {/* Rename Folder Modal */}
      <RenameFolderModal
        isOpen={renameModalOpen}
        onClose={handleRenameCancel}
        onRename={handleRename}
        currentName={renamingFolder?.name}
        isRenaming={isUpdatingFolder}
      />

      {/* Move File Modal */}
      <MoveFileModal
        isOpen={moveModalOpen}
        onClose={handleMoveCancel}
        onMove={handleMoveConfirm}
        folders={folders}
        currentFolderId={currentFolderId}
        fileName={fileToMove?.original_filename || ''}
        isMoving={isMovingFile}
      />

      {/* Delete Modal */}
      <FileDeleteModal
        isOpen={deleteModalOpen}
        file={itemToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Toast */}
      <Toast
        message={toastMessage}
        type={toastType}
        isOpen={!!toastMessage}
        onClose={() => setToastMessage('')}
      />
    </>
  );
};

UnifiedFileExplorer.propTypes = {
  accountId: PropTypes.string.isRequired,
  segmentedKnowledgeId: PropTypes.string,
  showUploader: PropTypes.bool,
  onNavigationChange: PropTypes.func,
  resetTrigger: PropTypes.number,
  onDragHandlersReady: PropTypes.func,
};

export { UnifiedFileExplorer };
